<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 分类代码 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        分类代码 <span class="text-red-500">*</span>
      </label>
      <input
        v-model="formData.categoryCode"
        type="text"
        required
        :disabled="isEdit"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 font-mono"
        placeholder="例如: speaking"
      />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ isEdit ? '分类代码创建后不可修改' : '使用小写字母和下划线，例如: speaking, writing, all' }}
      </p>
    </div>

    <!-- 分类名称 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        分类名称 <span class="text-red-500">*</span>
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          v-model="formData.name.zh"
          type="text"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          placeholder="中文，例如: 口语套餐"
        />
        <input
          v-model="formData.name.en"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          placeholder="English, e.g. Speaking Plans"
        />
      </div>
    </div>

    <!-- 分类描述 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        分类描述
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <textarea
          v-model="formData.description.zh"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 resize-none"
          placeholder="中文，例如: 专注于口语练习和提升"
        />
        <textarea
          v-model="formData.description.en"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 resize-none"
          placeholder="English description"
        />
      </div>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">可选，用于说明该分类的用途</p>
    </div>

    <!-- 显示顺序 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        显示顺序
      </label>
      <input
        v-model.number="formData.displayOrder"
        type="number"
        min="0"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
        placeholder="0"
      />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">数字越小越靠前，默认为 0</p>
    </div>

    <!-- 按钮 -->
    <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
      <button
        type="button"
        @click="handleCancel"
        class="cancel-btn"
      >
        取消
      </button>
      <button
        type="submit"
        class="primary-btn"
      >
        {{ isEdit ? '更新' : '创建' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { PlanCategory, CreateCategoryRequest, UpdateCategoryRequest } from '@/types/membership/category'
import { emptyLocalizedText, normalizeLocalizedText, type LocalizedText } from '@/types/membership/localized'

type CategoryFormData = Omit<CreateCategoryRequest, 'description'> & {
  description: LocalizedText
}

/**
 * Props
 */
interface Props {
  /** 套餐分类（编辑模式时传入） */
  category?: PlanCategory
  /** 是否为编辑模式 */
  isEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
})

/**
 * Emits
 */
const emit = defineEmits<{
  /** 提交表单 */
  submit: [data: CreateCategoryRequest | UpdateCategoryRequest]
  /** 取消操作 */
  cancel: []
}>()

/**
 * 表单数据
 */
const formData = ref<CategoryFormData>({
  categoryCode: '',
  name: emptyLocalizedText(),
  description: emptyLocalizedText(),
  displayOrder: 0,
})

/**
 * 监听 category prop 变化，用于编辑模式
 */
watch(
  () => props.category,
  (newCategory) => {
    if (newCategory) {
      formData.value = {
        categoryCode: newCategory.categoryCode,
        name: normalizeLocalizedText(newCategory.name),
        description: normalizeLocalizedText(newCategory.description),
        displayOrder: newCategory.displayOrder,
      }
    }
  },
  { immediate: true }
)

/**
 * 处理表单提交
 */
const handleSubmit = () => {
  if (props.isEdit) {
    // 编辑模式：只提交可修改的字段
    const updateData: UpdateCategoryRequest = {
      name: formData.value.name,
      description: formData.value.description,
      displayOrder: formData.value.displayOrder,
    }
    emit('submit', updateData)
  } else {
    // 创建模式：提交所有字段
    emit('submit', formData.value)
  }
}

/**
 * 处理取消操作
 */
const handleCancel = () => {
  emit('cancel')
}
</script>

<style scoped>
/* 可选：添加自定义样式 */
</style>
